import { Button } from '@/components/ui/Button';
import { mdiPlay } from '@mdi/js';
import * as React from 'react';
import type { Provider } from '../hooks/useImportJobs';

interface UrlInputProps {
  providers: Provider[];
  selectedProvider: string;
  onProviderChange: (provider: string) => void;
  urls: string;
  onUrlsChange: (value: string) => void;
  onSubmit: () => void;
  isLoading: boolean;
}

export const UrlInput: React.FC<UrlInputProps> = ({
  providers,
  selectedProvider,
  onProviderChange,
  urls,
  onUrlsChange,
  onSubmit,
  isLoading,
}) => {
  const parsedUrls = React.useMemo(() => {
    if (!urls.trim()) return [];
    return urls
      .split(/[\n]+/)
      .map(url => url.trim())
      .filter(url => url.length > 0 && url.startsWith('http'));
  }, [urls]);

  const selectedProviderData = providers.find(
    p => p.identifier === selectedProvider,
  );
  const isConfigured = selectedProviderData?.configured ?? false;

  const handleSubmit = () => {
    const confirmed = window.confirm(
      `Are you sure you want to import ${parsedUrls.length} place${parsedUrls.length !== 1 ? 's' : ''}?\n\nThis will create new businesses from the provided URLs.`,
    );
    if (confirmed) {
      onSubmit();
    }
  };

  return (
    <div className='space-y-4'>
      <div>
        <label className='block text-sm font-medium text-gray-700 mb-1'>
          Map Provider
        </label>
        <select
          value={selectedProvider}
          onChange={e => onProviderChange(e.target.value)}
          className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
        >
          {providers.map(provider => (
            <option key={provider.identifier} value={provider.identifier}>
              {provider.name}
              {!provider.configured && ' (Not Configured)'}
            </option>
          ))}
        </select>
        {!isConfigured && (
          <p className='mt-1 text-sm text-yellow-600'>
            This provider is not configured. Please set up the API key.
          </p>
        )}
      </div>

      <div>
        <label className='block text-sm font-medium text-gray-700 mb-1'>
          Map URLs
        </label>
        <textarea
          value={urls}
          onChange={e => onUrlsChange(e.target.value)}
          placeholder={`Paste ${selectedProviderData?.name || 'map'} URLs here, one per line...

Example:
https://maps.app.goo.gl/abc123
https://www.google.com/maps/place/Coffee+Shop/@40.7128,-74.0060,17z/...`}
          className='w-full h-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm'
        />
        <div className='mt-2 flex justify-between items-center'>
          <p className='text-sm text-gray-500'>
            {parsedUrls.length > 0 ? (
              <>
                <span className='font-semibold text-blue-600'>
                  {parsedUrls.length.toLocaleString()}
                </span>{' '}
                URL{parsedUrls.length !== 1 ? 's' : ''} detected
              </>
            ) : (
              'Paste URLs to import'
            )}
          </p>
        </div>
      </div>

      {selectedProviderData && (
        <div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
          <h4 className='text-sm font-medium text-blue-800 mb-2'>
            How to get URLs from {selectedProviderData.name}:
          </h4>
          <ol className='text-sm text-blue-700 list-decimal list-inside space-y-1'>
            <li>Open a place in {selectedProviderData.name}</li>
            <li>Click the share button and copy the link</li>
            <li>Paste each URL on a new line</li>
            <li>Both short links (maps.app.goo.gl) and full URLs work</li>
          </ol>
        </div>
      )}

      <div className='flex justify-end'>
        <Button
          label='Import'
          icon={mdiPlay}
          color='info'
          onClick={handleSubmit}
          disabled={parsedUrls.length === 0 || !isConfigured || isLoading}
        />
      </div>
    </div>
  );
};
